import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Trash2, Edit, Plus, Save, X } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const ProficiencyIconsManager = () => {
  const [icons, setIcons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingIcon, setEditingIcon] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    icon_url: '',
    order: 0,
    is_active: true
  });

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchIcons();
  }, []);

  const fetchIcons = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/proficiency-icons`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setIcons(data);
      }
    } catch (error) {
      console.error('Error fetching icons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/proficiency-icons`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Proficiency icon added successfully'
        });
        setShowAddForm(false);
        setFormData({ name: '', icon_url: '', order: 0, is_active: true });
        fetchIcons();
      } else {
        throw new Error('Failed to add icon');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to add proficiency icon',
        variant: 'destructive'
      });
    }
  };

  const handleUpdate = async (iconId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/proficiency-icons/${iconId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editingIcon)
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Proficiency icon updated successfully'
        });
        setEditingIcon(null);
        fetchIcons();
      } else {
        throw new Error('Failed to update icon');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update proficiency icon',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (iconId) => {
    if (!window.confirm('Are you sure you want to delete this proficiency icon?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/proficiency-icons/${iconId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Proficiency icon deleted successfully'
        });
        fetchIcons();
      } else {
        throw new Error('Failed to delete icon');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete proficiency icon',
        variant: 'destructive'
      });
    }
  };

  const startEdit = (icon) => {
    setEditingIcon({ ...icon });
  };

  const cancelEdit = () => {
    setEditingIcon(null);
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-3 sm:p-4 lg:p-6">
      <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Proficiency Icons</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base">Manage specialization icons shown on homepage and trainer signup</p>
        </div>
        <Button
          onClick={() => setShowAddForm(true)}
          className="bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900 w-full sm:w-auto"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Icon
        </Button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <Card className="mb-6 border-2 border-red-200">
          <CardHeader className="bg-gradient-to-r from-red-50 to-gray-50">
            <CardTitle className="flex items-center justify-between">
              <span>Add New Proficiency Icon</span>
              <Button variant="ghost" size="sm" onClick={() => setShowAddForm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Icon Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Yoga, Fitness"
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Icon (Emoji)</Label>
                <Input
                  value={formData.icon_url}
                  onChange={(e) => setFormData({ ...formData, icon_url: e.target.value })}
                  placeholder="e.g., 🧘, 💪"
                  className="mt-2 text-2xl"
                />
              </div>
              <div>
                <Label>Order</Label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  className="mt-2"
                />
              </div>
              <div className="flex items-center space-x-2 mt-8">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
            </div>
            <Button onClick={handleAdd} className="mt-4">
              <Save className="w-4 h-4 mr-2" />
              Save Icon
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Icons Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {icons.map((icon) => (
          <Card key={icon.id} className={`overflow-hidden border-2 ${
            icon.is_active ? 'border-green-200' : 'border-gray-200 dark:border-gray-700'
          }`}>
            <CardContent className="p-4">
              {editingIcon && editingIcon.id === icon.id ? (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Name</Label>
                    <Input
                      value={editingIcon.name}
                      onChange={(e) => setEditingIcon({ ...editingIcon, name: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Icon</Label>
                    <Input
                      value={editingIcon.icon_url}
                      onChange={(e) => setEditingIcon({ ...editingIcon, icon_url: e.target.value })}
                      className="mt-1 text-2xl"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Order</Label>
                    <Input
                      type="number"
                      value={editingIcon.order}
                      onChange={(e) => setEditingIcon({ ...editingIcon, order: parseInt(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={editingIcon.is_active}
                      onChange={(e) => setEditingIcon({ ...editingIcon, is_active: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label className="text-xs">Active</Label>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleUpdate(icon.id)} className="flex-1">
                      <Save className="w-3 h-3 mr-1" />
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={cancelEdit} className="flex-1">
                      <X className="w-3 h-3 mr-1" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-center mb-3">
                    <div className="text-5xl mb-2">{icon.icon_url}</div>
                    <h3 className="font-bold text-lg">{icon.name}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Order: {icon.order}</p>
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      icon.is_active ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {icon.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => startEdit(icon)}
                      className="flex-1"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(icon.id)}
                      className="flex-1"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        ))}

        {icons.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400">
            <p className="text-lg font-semibold mb-2">No proficiency icons yet</p>
            <p className="text-sm">Click "Add Icon" to create your first proficiency icon</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProficiencyIconsManager;